/**
 * <back-in-stock-form>
 *
 * Handles submission of the "Notify me when it's back in stock" form.
 * Submits phone + requested size (plus product context) to a Google Apps
 * Script Web App endpoint, which is responsible for logging the request to
 * a Google Sheet and emailing the store.
 *
 * The endpoint URL is read from the `data-endpoint` attribute, which is
 * rendered from the `back_in_stock_webhook_url` theme setting.
 *
 * Note: requests are sent with mode "no-cors" because Google Apps Script
 * Web Apps don't return CORS headers. This means we can't read the response
 * back (so we can't detect application-level errors reported by the script),
 * but we can still detect network failures. We optimistically show a success
 * message once the request has been sent without a network error.
 */
class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');

    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.bannerRegion = this.querySelector('[data-back-in-stock-banner]');

    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    event.preventDefault();

    this.clearBanner();

    const endpoint = this.dataset.endpoint;

    if (!endpoint) {
      this.showBanner(
        "This form isn't fully set up yet. Please contact us directly and we'll notify you by hand.",
        'error'
      );
      return;
    }

    if (!this.form.reportValidity()) {
      return;
    }

    const formData = new FormData(this.form);
    const payload = {
      phone: (formData.get('phone') || '').toString().trim(),
      size: (formData.get('size') || '').toString().trim(),
      productTitle: this.dataset.productTitle || '',
      productVendor: this.dataset.productVendor || '',
      productId: this.dataset.productId || '',
      productUrl: this.dataset.productUrl || '',
      pageUrl: window.location.href,
      submittedAt: new Date().toISOString()
    };

    this.setLoading(true);

    try {
      await fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      });

      this.form.hidden = true;
      this.showBanner(
        "Thanks! We've saved your number and will reach out as soon as this size is back in stock.",
        'success'
      );
    } catch (error) {
      this.setLoading(false);
      this.showBanner(
        'Something went wrong sending your request. Please try again in a moment.',
        'error'
      );
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.submitButton.dataset.originalText = this.submitButton.dataset.originalText || this.submitButton.textContent;
      this.submitButton.textContent = 'Sending...';
      this.submitButton.disabled = true;
    } else {
      this.submitButton.textContent = this.submitButton.dataset.originalText || this.submitButton.textContent;
      this.submitButton.disabled = false;
    }
  }

  showBanner(content, status) {
    if (!this.bannerRegion) return;
    this.bannerRegion.innerHTML = `<div class="banner banner--${status} text-start">${content}</div>`;
  }

  clearBanner() {
    if (this.bannerRegion) this.bannerRegion.innerHTML = '';
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}